import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useAuth0 } from '@auth0/auth0-react';
import { Box } from '@mui/material';
import { endOfWeek, startOfWeek } from 'date-fns';

import CalendarHeader from './CalendarHeader';
import CalendarBody from './CalendarBody';
import { FHWeekDatesPicker } from '../../base';
import PageContainer from '../PageContainer';
import {
  btSupervisionActions,
  clientAssessmentActions,
  clientSupervisionActions,
  groupParentSupervisionActions,
  parentSupervisionActions,
  sessionActions,
} from '../../../store';
import { areUserRolesIn } from '../../../helpers';
import {
  DocumentMapEnum,
  HD_CALENDAR_WEEK_DATE_LS_KEY,
  ROLE_BASED_ALLOWED_CALLS,
} from './constants';
import { Roles } from '../../../constants';
import { initializeCalendarData } from './utils';
import { FORTA_BLUE } from '../../../utils/colors';

import type { RootState } from '../../../store';
import type {
  IBtSupervision,
  IClientAssessment,
  IClientSupervision,
  IGroupSupervision,
  IParentSupervision,
  ISessionMinified,
} from '../../../utils/types';

export interface ICalendarData {
  [DocumentMapEnum.ASSESSMENT]: IClientAssessment[];
  [DocumentMapEnum.BT_SN]: IBtSupervision[];
  [DocumentMapEnum.CLIENT_SN]: IClientSupervision[];
  [DocumentMapEnum.GROUP_TRAINING]: IGroupSupervision[];
  [DocumentMapEnum.PARENT_SN]: IParentSupervision[];
  [DocumentMapEnum.SESSION]: ISessionMinified[];
}

function HistoricalDocumentCalendar() {
  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useSelector((state: RootState) => state.auth);

  const initialDateToSelect = useMemo(() => {
    const paramDate = searchParams.get(HD_CALENDAR_WEEK_DATE_LS_KEY);
    const persistedDate = sessionStorage.getItem(HD_CALENDAR_WEEK_DATE_LS_KEY);
    const dateStr = paramDate || persistedDate; // Higher priority is given to date fetched from params
    const dateVal = dateStr ? new Date(decodeURIComponent(dateStr)) : new Date();
    return dateVal;
  }, []);

  const [loading, setLoading] = useState<boolean>(false);
  const [weekPickerDate, setWeekPickerDate] = useState<Date>(initialDateToSelect);
  const [calendarData, setCalendarData] = useState<ICalendarData>(() => initializeCalendarData());
  const [calendarDate, setCalendarDate] = useState<{ startDate: Date; endDate: Date }>({
    startDate: startOfWeek(initialDateToSelect),
    endDate: endOfWeek(initialDateToSelect),
  });

  const [sun, mon, tue, wed, thu, fri, sat] = useMemo(() => {
    const weekStart = calendarDate.startDate;
    const sun = moment(weekStart);
    const mon = moment(weekStart).add(1, 'day');
    const tue = moment(weekStart).add(2, 'day');
    const wed = moment(weekStart).add(3, 'day');
    const thu = moment(weekStart).add(4, 'day');
    const fri = moment(weekStart).add(5, 'day');
    const sat = moment(weekStart).add(6, 'day');
    return [sun, mon, tue, wed, thu, fri, sat];
  }, [calendarDate.startDate]);

  useEffect(() => {
    persistDateAndUpdateParams(weekPickerDate);
  }, []);

  useEffect(() => {
    async function fetchDocumentData() {
      const isBCBA = areUserRolesIn(user?.roles, [Roles.Bcba]);
      if (!calendarDate || !isBCBA) return;

      setLoading(true);
      const allowedCalls = ROLE_BASED_ALLOWED_CALLS.bcba;

      const params = {
        startDate: moment(calendarDate.startDate).startOf('day').subtract(1, 'day'),
        endDate: moment(calendarDate.endDate).endOf('day'),
      };

      const documentPromises: (Promise<any> | null)[] = [];

      // Assessment call
      if (allowedCalls.includes(DocumentMapEnum.ASSESSMENT)) {
        documentPromises.push(
          dispatch(clientAssessmentActions.getList({ getAccessTokenSilently, params })),
        );
      } else {
        documentPromises.push(null);
      }

      // BT SN call
      if (allowedCalls.includes(DocumentMapEnum.BT_SN)) {
        documentPromises.push(
          dispatch(btSupervisionActions.getList({ getAccessTokenSilently, params })),
        );
      } else {
        documentPromises.push(null);
      }

      // Client SN call
      if (allowedCalls.includes(DocumentMapEnum.CLIENT_SN)) {
        documentPromises.push(
          dispatch(clientSupervisionActions.getList({ getAccessTokenSilently, params })),
        );
      } else {
        documentPromises.push(null);
      }

      // Group Parent training call
      if (allowedCalls.includes(DocumentMapEnum.GROUP_TRAINING)) {
        documentPromises.push(
          dispatch(groupParentSupervisionActions.getList({ getAccessTokenSilently, params })),
        );
      } else {
        documentPromises.push(null);
      }

      // Parent SN call
      if (allowedCalls.includes(DocumentMapEnum.PARENT_SN)) {
        documentPromises.push(
          dispatch(parentSupervisionActions.getList({ getAccessTokenSilently, params })),
        );
      } else {
        documentPromises.push(null);
      }

      // Session call
      if (allowedCalls.includes(DocumentMapEnum.SESSION)) {
        documentPromises.push(
          dispatch(
            sessionActions.getList({
              getAccessTokenSilently,
              params: { ...params, include_qa_stats: true },
            }),
          ),
        );
      } else {
        documentPromises.push(null);
      }

      const [assessmentData, btSNData, clientSNData, groupSNData, parentSNData, sessionData] =
        await Promise.all(documentPromises);

      setCalendarData({
        [DocumentMapEnum.ASSESSMENT]: assessmentData?.payload ?? [],
        [DocumentMapEnum.BT_SN]: btSNData?.payload ?? [],
        [DocumentMapEnum.CLIENT_SN]: clientSNData?.payload ?? [],
        [DocumentMapEnum.GROUP_TRAINING]: groupSNData?.payload ?? [],
        [DocumentMapEnum.PARENT_SN]: parentSNData?.payload ?? [],
        [DocumentMapEnum.SESSION]: sessionData?.payload ?? [],
      });
      setLoading(false);
    }

    fetchDocumentData();
  }, [calendarDate, user]);

  const handleDateSelect = (date: Date, startDate?: Date, endDate?: Date) => {
    setWeekPickerDate(date);
    persistDateAndUpdateParams(date);
    setCalendarDate({
      startDate: startDate ? startDate : startOfWeek(date),
      endDate: endDate ? endDate : endOfWeek(date),
    });
  };

  const persistDateAndUpdateParams = (date: Date) => {
    sessionStorage.setItem(HD_CALENDAR_WEEK_DATE_LS_KEY, date.toISOString());
    const dateParams = {
      [HD_CALENDAR_WEEK_DATE_LS_KEY]: encodeURIComponent(date.toISOString()),
    };
    setSearchParams(dateParams);
  };

  return (
    <PageContainer sx={{ px: 3, pt: 2 }} dataTestId="historicalDocumentsCalendarView">
      <Box>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between' }}>
          <FHWeekDatesPicker
            data-testid="historical-docs-date-picker"
            containerStyles={{ borderRadius: '10px', backgroundColor: FORTA_BLUE.light1 }}
            value={weekPickerDate}
            disableNavigation={loading}
            onChange={(date: Date, startDate: Date, endDate: Date) => {
              handleDateSelect(date, startDate, endDate);
            }}
            handlerWidth={540}
          />
        </Box>
        <CalendarHeader week={[sun, mon, tue, wed, thu, fri, sat]} />
        <CalendarBody
          data={calendarData}
          week={[sun, mon, tue, wed, thu, fri, sat]}
          loading={loading}
        />
      </Box>
    </PageContainer>
  );
}

export default HistoricalDocumentCalendar;
